import { Text } from "@imminently/react/ui/core/text";
import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { useCallback, useMemo, useState } from "react";
import Lightbox from "yet-another-react-lightbox";

import { css } from "@/theme";
import { PatientEncounter, Problem } from "@/resources/types";
import { PatientEncounterChangeListener } from "../../pages/patient/patient";
import { Divider, InputBase, Stack } from "@mui/material";
import { ComplaintCodeCard } from "@/components/ComplaintCode/ComplaintCodeList";

import "yet-another-react-lightbox/styles.css";

const styles = {
  tabHeading: css`
    & .MuiTypography-h4 {
      display: inline-block;
      vertical-align: middle;
    }

    & .MuiTabs-root {
      display: inline-block;
      vertical-align: middle;
      background-color: #c3c8de;
      border-radius: 20px;
      border: 3px solid #c3c8de;
      padding: 0px;
      min-height: inherit;
      margin-left: 30px;
    }
  `,
  buttonGroup: css`
    & .MuiButtonBase-root {
      padding: 0px;
      min-height: 29px;
      font-size: 12px;
      font-family: "Lato";
      font-style: normal;
      font-weight: 400;
      color: #161b32;
    }

    & .Mui-selected {
      background-color: #fafbff;
      border-radius: 20px;
    }

    & .MuiTabs-scroller {
      vertical-align: middle;
    }

    & .MuiTabs-indicator {
      display: none;
    }
  `,
};

type ProblemInputProps = {
  id: keyof Problem;
  encounter: PatientEncounter;
  disabled?: boolean;
  onEncounterChange: PatientEncounterChangeListener;
};

const ProblemInput = (props: ProblemInputProps) => {
  const { encounter, onEncounterChange, id } = props;

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onEncounterChange(`problem.${id}`, event.target.value);
    },
    []
  );

  return (
    <InputBase
      sx={{ width: "100%" }}
      placeholder="None"
      defaultValue={encounter.problem?.[id]}
      onChange={handleChange}
      multiline
      minRows={1}
      disabled={props.disabled ? props.disabled : false}
    />
  );
};

enum TabNames {
  TRIAGE = "Triage",
  PATIENTS = "Patients",
  AMBULANCE = "Ambulance",
  EXPECT = "Expect",
}

type PatientMedicalNotesProps = {
  encounter: PatientEncounter;
  onEncounterChange: PatientEncounterChangeListener;
};

const PatientComplaintCode = ({ encounter }: { encounter: PatientEncounter }) => {
  const complaintCode = (encounter as any).complaintCode;
  if (!complaintCode) return null;

  return <ComplaintCodeCard value={complaintCode} selected className="w-fit max-w-full" />;
};

const PatientMedicalNotes = (props: PatientMedicalNotesProps) => {
  // Determine which tab to focus initially. Default to patient tab. Use
  // `useMemo` so we don't get the tab switch on initial page load.
  const initialTab = useMemo(() => {
    const { problem } = props.encounter;

    if (problem.triage) {
      return 0;
    } else if (problem.patient) {
      return 1;
    } else if (problem.ambulance) {
      return 2;
    } else if (problem.expects) {
      return 3;
    }

    return 1;
  }, []);

  const [value, setValue] = useState(initialTab);
  const [selectedImage, setSelectedImage] = useState<number | undefined>(
    undefined
  );

  const { encounter, onEncounterChange } = props;
  const currentTab = Object.values(TabNames)[value];
  const triageNurseName = `${encounter.triageNurse?.firstName?.trim() || ""} ${encounter.triageNurse?.lastName?.trim() || ""}`.trim();

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const problemPhotos = encounter.photos?.filter(
    (photo) => photo.type === "problem"
  );

  const inputsDisabled =
    encounter.status === "discharged" ||
    encounter.status === "disabled" ||
    !!encounter.triaged;

  return (
    <Stack gap={3}>
      <Box css={styles.tabHeading}>
        <Text variant="h4">Presenting Problem</Text>
        <Tabs css={styles.buttonGroup} value={value} onChange={handleChange}>
          {Object.values(TabNames).map((tab, index) => (
            <Tab key={index} label={tab} />
          ))}
        </Tabs>
      </Box>

      <PatientComplaintCode encounter={encounter} />

      <Box>
        {currentTab === TabNames.TRIAGE && (
          <>
            <ProblemInput
              id="triage"
              encounter={encounter}
              onEncounterChange={onEncounterChange}
              disabled={inputsDisabled}
            />
            {triageNurseName ? <Text variant="body2">Triage nurse: {triageNurseName}</Text> : null}
            {encounter.problem?.nursingAssessment && (
              <>
            <Divider style={{ marginTop: 10, marginBottom: 10 }} />
                <h4 style={{ marginTop: 10 }}>Nursing Assessment</h4>
                <ProblemInput
                  id="nursingAssessment"
                  encounter={encounter}
                  onEncounterChange={onEncounterChange}  
                  disabled={inputsDisabled}
                />
              </>
            )}
          </> 
        )}
        {currentTab === TabNames.PATIENTS && (
          <>
            <ProblemInput
              id="patient"
              encounter={encounter}
              disabled={true}
              onEncounterChange={onEncounterChange}
            />
            {problemPhotos?.length ? (
              <>
                <Box display="flex" gap={1} mt={3}>
                  {problemPhotos.map((photo, index) => (
                    <img
                      key={photo.data}
                      style={{ borderRadius: 8 }}
                      width={80}
                      height={80}
                      src={photo.data}
                      onClick={() => setSelectedImage(index)}
                    />
                  ))}
                </Box>
                <Lightbox
                  styles={{ container: { background: "rgba(0,0,0,0.5)" } }}
                  index={selectedImage}
                  carousel={{ finite: true, imageFit: "cover" }}
                  open={typeof selectedImage === "number"}
                  close={() => setSelectedImage(undefined)}
                  slides={problemPhotos?.map((photo) => ({
                    src: photo.data,
                    width: 640,
                    height: 640,
                  }))}
                />
              </>
            ) : null}
          </>
        )}
        {currentTab === TabNames.AMBULANCE && (
          <ProblemInput
            id="ambulance"
            encounter={encounter}
            onEncounterChange={onEncounterChange}
            disabled={inputsDisabled}
          />
        )}
        {currentTab === TabNames.EXPECT && (
          <ProblemInput
            id="expects"
            encounter={encounter}
            onEncounterChange={onEncounterChange}
            disabled={inputsDisabled}
          />
        )}
      </Box>
    </Stack>
  );
};

export default PatientMedicalNotes;
